import React from 'react';
import { motion } from 'framer-motion';
import { FaUsers } from 'react-icons/fa';

type Member = {
  name: string;
  title?: string;
  category: 'chair' | 'secretary' | 'board' | 'admin';
  image?: string;
};

const MEMBERS: Member[] = [
  { name: 'Amb. Rasheed Seidu Inusah', category: 'chair', image: '/teamPics/amb.jpg' },
  { name: 'Alhaji Abdul Basit Rasheed Inusah', category: 'secretary', image: '/teamPics/bashit.jpg' },
 // { name: 'Alhaji Shehu Haruna Inusah', category: 'board', image: '/teamPics/shehu.JPG' },
  { name: 'Alhaji Rasheed Ibrahim Inusah', category: 'board', image: '/teamPics/ibrahim.jpg' },
  { name: 'Alhaji Mohammed Yasin Rasheed Inusah', category: 'board', image: '/teamPics/yasin.JPG' },
  { name: 'Hajia Zainab Inusah', category: 'board', image: '/teamPics/zainab.jpg' },
  { name: 'Hajia Munaya Inusah', category: 'board', image: '/teamPics/Munaya.jpg' },
  { name: 'Mr. Kusi Frederick', title: 'Resident Administrator', category: 'admin', image: '/teamPics/fred.png' }
];

const getInitials = (name: string) =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('') || 'AI';

const Team: React.FC = () => {
  const chair = MEMBERS.find(m => m.category === 'chair');
  const secretary = MEMBERS.find(m => m.category === 'secretary');
  const admin = MEMBERS.find(m => m.category === 'admin');
  const boardMembers = MEMBERS.filter(m => m.category === 'board');

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Header */}
      <div className="relative bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-900 pt-6 pb-8">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 backdrop-blur-sm text-cyan-300 rounded-full text-sm font-semibold mb-4">
              <FaUsers className="text-xs" />
              Leadership Team
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              Foundation Leadership
            </h1>

            <p className="text-base md:text-lg text-gray-300 mb-4">
              Alhaji Inusah Foundation Community Centre
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-4xl py-8">

        {/* Compact Leadership */}
        <div className="space-y-6 mb-8">
          {chair && secretary && (
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex flex-col items-center gap-3 text-center">
                  {chair.image ? (
                    <img
                      src={chair.image}
                      alt={chair.name}
                      className="w-16 h-16 rounded-full object-cover shadow-sm"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-bold">
                      {getInitials(chair.name)}
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{chair.name}</h3>
                    <p className="text-xs text-gray-500">Chair</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex flex-col items-center gap-3 text-center">
                  {secretary.image ? (
                    <img
                      src={secretary.image}
                      alt={secretary.name}
                      className="w-16 h-16 rounded-full object-cover shadow-sm"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white font-bold">
                      {getInitials(secretary.name)}
                    </div>
                  )}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{secretary.name}</h3>
                    <p className="text-xs text-gray-500">Secretary</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Board Members Grid */}
          <div className="relative bg-gray-50 rounded-lg p-6">
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-full blur-3xl"></div>
            </div>

            <h3 className="text-sm font-semibold text-gray-900 mb-4 text-center">Board Members</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {boardMembers.map((member) => (
                <div key={member.name} className="bg-white rounded-lg p-3 shadow-sm border border-gray-100">
                  <div className="flex flex-col items-center gap-3 text-center">
                    {member.image ? (
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-16 h-16 rounded-full object-cover shadow-sm"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center text-white font-bold">
                        {getInitials(member.name)}
                      </div>
                    )}
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 leading-tight">{member.name}</h4>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Administrator */}
          {admin && (
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4">
              <div className="flex flex-col items-center gap-3 text-center">
                {admin.image ? (
                  <img
                    src={admin.image}
                    alt={admin.name}
                    className="w-16 h-16 rounded-full object-cover shadow-sm"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-bold">
                    {getInitials(admin.name)}
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{admin.name}</h3>
                  <p className="text-xs text-emerald-700">{admin.title}</p>
                  <p className="text-xs text-gray-600 mt-1">+233 5498 70386</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="text-center text-xs text-gray-500 mt-8 pt-6 border-t border-gray-200">
          <p>Continuing the legacy of Alhaji Inusah Adamu (1913-2014)</p>
        </div>
      </div>
    </div>
  );
};

export default Team;
